import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Tag, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { internalHref } from '../utils/internalHref';
// Build-time-generated blog data, imported synchronously so posts are present
// during SSR/prerender (and on the client's first render, matching hydration).
// Generated by scripts/generate-blog-data.js from content/blog/*.md.
import blogData from '../data/blog-posts.json';

// Define a type for blog posts
export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  author: string;
  category: string;
  excerpt: string;
  thumbnail: string;
  tags: string[];
  content?: string;
  seo_description?: string;
  seo_keywords?: string;
}

// Populated at module load from the build-time-generated JSON so it is
// available during SSR/prerender and on the client without a fetch.
export const SAMPLE_BLOG_POSTS: BlogPost[] = blogData as BlogPost[];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

const BlogPostCard: React.FC<{post: BlogPost}> = ({ post }) => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="h-48 overflow-hidden relative">
        <img 
          src={post.thumbnail} 
          alt={post.title} 
          className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
        />
        <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 m-2 rounded text-sm">
          {post.category}
        </div>
      </div>
      <div className="p-5">
        <h2 className="text-xl font-semibold mb-2 text-gray-800 hover:text-blue-600">
          <Link to={internalHref(`/blog/${post.slug}`)}>{post.title}</Link>
        </h2>
        
        <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
          <div className="flex items-center">
            <Calendar size={14} className="mr-1" />
            <span>{formatDate(post.date)}</span>
          </div>
          <div className="flex items-center">
            <User size={14} className="mr-1" />
            <span>{post.author}</span>
          </div>
        </div>
        
        <p className="text-gray-600 mb-4">{post.excerpt}</p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map(tag => (
            <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
              <Tag size={12} className="inline mr-1" />
              {tag}
            </span>
          ))}
        </div>
        
        <Link 
          to={internalHref(`/blog/${post.slug}`)} 
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          Read more <ChevronRight size={16} className="ml-1" />
        </Link>
      </div>
    </div>
  );
};

const BlogPage: React.FC = () => {
  // Data is baked in at build time, so it renders synchronously (SSR + client).
  const blogPosts = SAMPLE_BLOG_POSTS;

  return (
    <div className="container mx-auto px-4 py-8">
      <SEOHead
        title="Car Loan Blog | Tips and Advice on Car Financing in Australia"
        description="Read the latest articles on car loans, financing options, and tips to get the best deal on your next car purchase in Australia."
        keywords="australian car loans blog, car financing tips, car loan advice australia, vehicle finance guide"
      />
      
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Car Loan Blog</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Expert tips, guides, and advice on car loans and vehicle financing in Australia
        </p>
      </div>
      
      {blogPosts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map(post => (
            <BlogPostCard key={post.slug} post={post} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4">No Blog Posts Available Yet</h2>
          <p className="text-gray-600 mb-6">
            We're working on creating valuable content about car loans and financing. 
            Check back soon for expert tips and advice!
          </p>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
          <Link 
            to="/car-loan-calculator/" 
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
          >
            Try Our Car Loan Calculator
          </Link>
        </div>
      )}
      
      <div className="mt-12 text-center">
        <p className="text-gray-600 mb-4">
          Looking for specific information about car loans? 
          Try our comprehensive calculators to estimate your repayments.
        </p>
        <Link 
          to="/car-loan-calculator/" 
          className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Use Our Car Loan Calculator
        </Link>
      </div>
    </div>
  );
};

export default BlogPage; 